import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { catchError, defer, switchMap, take, throwError } from 'rxjs';
import { UserType } from '../../types';
import { GlobalDataService, SessionData } from '../global-data/global-data.service';
import { MainService } from '../main.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { RawAuthResponse } from './auth.service';
import { SamSessionService } from './sam-session.service';

@Injectable({ providedIn: 'root' })
export class HqSessionRecoveryService {
  private readonly global = inject(GlobalDataService);
  private readonly http = inject(HttpClient);
  private readonly main = inject(MainService);
  private readonly msal = inject(MsalService);
  private readonly snackbar = inject(SnackbarService);
  private readonly samSession = inject(SamSessionService);
  private pending?: { session: SessionData; generation: number; account: any | null; notice?: MatSnackBarRef<TextOnlySnackBar>; busy: boolean; action: ReturnType<SnackbarService['holdAction']> };

  constructor() {
    this.global.getSessionData().subscribe(() => {
      if (this.pending && !this.current(this.pending.session, this.pending.generation)) {
        this.pending.action.release();
        this.pending = undefined;
      }
    });
  }

  beginRecovery(failedToken: string): void {
    const session = this.global.getSessionData().value;
    if (!session.initialized || session.userType !== UserType.ActiveDirectory || session.sessionVersion !== failedToken) return;
    const generation = this.global.getAuthenticationGeneration();
    if (this.pending && this.current(this.pending.session, this.pending.generation)) return;
    this.pending?.action.release();
    const pending = this.pending = { session, generation, account: this.msal.instance.getActiveAccount(), busy: false, action: this.snackbar.holdAction() } as NonNullable<typeof this.pending>;
    this.login(pending, true);
  }

  private current(session: SessionData, generation: number): boolean {
    const current = this.global.getSessionData().value;
    return this.global.isAuthenticationCurrent(generation) && current.initialized &&
      current.userType === UserType.ActiveDirectory && current.userId === session.userId && current.sessionVersion === session.sessionVersion;
  }

  private offer(pending: NonNullable<HqSessionRecoveryService['pending']>, message: string): void {
    if (!this.current(pending.session, pending.generation)) return;
    pending.notice = pending.action.open(message, 'Log ind igen');
    pending.notice.onAction().pipe(take(1)).subscribe(() => this.login(pending));
  }

  private login(pending: NonNullable<HqSessionRecoveryService['pending']>, silent = false): void {
    if (pending.busy || !this.current(pending.session, pending.generation)) return;
    pending.busy = true;
    if (!silent) pending.generation = this.global.beginAuthentication();
    const request = { scopes: ['user.read'], loginHint: pending.account?.username };
    const acquire = silent
      ? defer(() => pending.account
        ? this.msal.acquireTokenSilent({ ...request, account: pending.account, forceRefresh: true })
        : this.msal.ssoSilent(request))
      : defer(() => this.msal.loginPopup(request));
    acquire.pipe(
      take(1),
      switchMap(result => {
        if (!result.idToken || !this.current(pending.session, pending.generation) ||
            pending.account && result.account?.homeAccountId !== pending.account.homeAccountId) return throwError(() => new Error('Stale login'));
        return this.http.post<RawAuthResponse>(this.main.getApiURL() + 'User/Authenticate', {
          expectedUserId: pending.session.userId, expectedSessionVersion: pending.session.sessionVersion,
        }, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${result.idToken}` }),
          withCredentials: true,
        }).pipe(switchMap(response => {
          if (response.userType !== UserType.ActiveDirectory || response.id !== pending.session.userId) {
            return throwError(() => new Error('Different user'));
          }
          if (this.current(pending.session, pending.generation)) {
            this.samSession.authenticated(response);
          }
          return [];
        }));
      })
    ).subscribe({
      error: (error: unknown) => {
        pending.busy = false;
        if (error instanceof HttpErrorResponse && error.status === 409 && error.error?.code === 'sam_session_changed') {
          pending.action.release();
          if (this.pending === pending) this.pending = undefined;
          void this.samSession.refresh();
          return;
        }
        if (!this.current(pending.session, pending.generation)) {
          pending.action.release();
          if (this.pending === pending) this.pending = undefined;
          return;
        }
        this.offer(pending, silent ? 'Sessionen kunne ikke fornyes automatisk. Log ind igen for at fortsætte.' : 'Login blev ikke gennemført med samme bruger. Prøv igen.');
      },
      complete: () => { pending.action.release(); if (this.pending === pending) this.pending = undefined; },
    });
  }
}
